import React, { useEffect, useState } from 'react';
import { getUserFriendlyError } from '../utils/errorMessages';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { getLogo, BRANDING } from '../config/branding';
import { useLocale } from '../context/LocaleContext';

const SignIn = () => {
  const navigate = useNavigate();
  const { loginWithGoogle, login, isAuthenticated, loading } = useAuth();
  const { t } = useLocale();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Clear any stale error when component mounts
  useEffect(() => {
    setError('');
  }, []);

  useEffect(() => {
    if (isAuthenticated && !loading) {
      navigate('/home');
    }
  }, [isAuthenticated, loading, navigate]);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      await login(email, password);
      navigate('/home');
    } catch (err) {
      setError(getUserFriendlyError(err, 'Unable to sign in. Please check your email and password.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-white flex items-center justify-center">
        <div className="text-center">
          <img
            src={getLogo()}
            alt={BRANDING.app.name}
            className="h-16 w-16 mx-auto mb-4 rounded-2xl"
          />
          <p className="text-gray-600">{t('common.loading') || 'Loading...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50/80 via-cyan-50/60 to-white px-6 py-10">
      <div className="max-w-sm mx-auto animate-fade-in-up">
        {/* Logo & Brand */}
        <div className="text-center mb-8">
          <div className="relative inline-block">
            <img
              src={getLogo()}
              alt={BRANDING.app.name}
              className="h-20 w-20 mx-auto mb-4 rounded-2xl shadow-soft-lg transition-transform hover:scale-105"
            />
            <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-full flex items-center justify-center shadow-lg">
              <span className="text-white text-xs">✨</span>
            </div>
          </div>
          <h1 className="text-2xl font-semibold text-gradient-purple mb-2">
            {BRANDING.app.name}
          </h1>
          <p className="text-gray-600 text-sm leading-relaxed max-w-xs mx-auto">
            {t('auth.tagline') || 'I remember how yesterday felt'}<br/>{t('auth.tagline2') || 'and help you live today better.'}
          </p>
        </div>

        {/* Sign In Form */}
        <div className="glass-strong rounded-3xl p-7 shadow-soft-lg border border-white/60 animate-scale-in">
          <h2 className="text-xl font-semibold text-gray-800 mb-1">{t('auth.welcomeBack') || 'Welcome back'}</h2>
          <p className="text-gray-400 text-sm mb-6">{t('auth.goodToSeeYou') || 'Good to see you again'} 💜</p>
          
          <form onSubmit={handleEmailLogin} className="space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-sm font-medium text-gray-600">{t('auth.email') || 'Email'}</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={submitting}
                className="h-12 bg-white/80 border-gray-200/80 focus:border-teal-400 focus:ring-teal-400/20 rounded-xl transition-all placeholder:text-gray-400"
              />
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-sm font-medium text-gray-600">{t('auth.password') || 'Password'}</Label>
                <Link to="/forgot-password" className="text-xs text-teal-600 hover:text-teal-700 font-medium transition-colors">
                  {t('auth.forgotPassword') || 'Forgot password?'}
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={submitting}
                className="h-12 bg-white/80 border-gray-200/80 focus:border-teal-400 focus:ring-teal-400/20 rounded-xl transition-all placeholder:text-gray-400"
              />
            </div>

            {error && (
              <Alert variant="destructive" className="border-red-200/60 bg-red-50/80 py-3 rounded-xl animate-fade-in">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-sm">{error}</AlertDescription>
              </Alert>
            )}

            <Button
              type="submit"
              className="w-full h-12 bg-gray-900 hover:bg-gray-800 text-white rounded-xl font-medium shadow-soft transition-all hover:shadow-soft-lg hover:-translate-y-0.5 active:translate-y-0"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('auth.signingIn') || 'Signing in...'}
                </>
              ) : (
                t('auth.signIn') || 'Sign In'
              )}
            </Button>
          </form>

          {/* Sign Up Link */}
          <div className="text-center mt-7 pt-6 border-t border-gray-100/60">
            <p className="text-gray-500 text-sm">
              {t('auth.newToApp') || 'New to LifeFlo?'}{' '}
              <Link to="/signup" className="text-teal-600 hover:text-teal-700 font-semibold transition-colors">
                {t('auth.createAccount') || 'Create an account'}
              </Link>
            </p>
          </div>
        </div>

        {/* Bottom warm message */}
        <div className="py-8 text-center">
          <p className="text-gray-400 text-sm font-light">
            🌱 {t('home.takeYourTime')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
